import { useEffect, useRef } from "react";

import type { Figure as BdlFigure } from "../bdl/model";
import { paintFigure } from "../bdl/painter";

/**
 * A BDL figure on a canvas. Animates with requestAnimationFrame when
 * `animate` is set and the figure has a timeline; otherwise draws one frame
 * at `t`. Only animates while on screen (IntersectionObserver) — the same
 * rule the app follows.
 */
export function Figure({ figure, size = 300, animate = true, t = 0, className }: {
  figure: BdlFigure; size?: number; animate?: boolean; t?: number; className?: string;
}) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    canvas.width = size * dpr; canvas.height = size * dpr;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const draw = (tt: number) => { ctx.setTransform(dpr, 0, 0, dpr, 0, 0); paintFigure(ctx, figure, size, size, tt); };

    const duration = figure.timeline?.duration ?? 0;
    if (!animate || duration <= 0) { draw(t); return; }

    let raf = 0, visible = true;
    const t0 = performance.now() - t * duration;
    const loop = (now: number) => { if (visible) draw(((now - t0) % duration) / duration); raf = requestAnimationFrame(loop); };
    const io = new IntersectionObserver(([e]) => { visible = !!e?.isIntersecting; }, { threshold: 0.05 });
    io.observe(canvas);
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); io.disconnect(); };
  }, [figure, size, animate, t]);

  return <canvas ref={ref} className={className} style={{ width: size, height: size, display: "block" }} aria-hidden />;
}
